import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

// Idle-timeout window in milliseconds. Customers: 45 min. Staff: 8 hours (no idle logout).
const CUSTOMER_IDLE_MS = 45 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('besq_token');
    const savedUser = localStorage.getItem('besq_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const persistSession = (newToken, newUser) => {
    localStorage.setItem('besq_token', newToken);
    localStorage.setItem('besq_user', JSON.stringify(newUser));
    localStorage.setItem('besq_last_activity', Date.now().toString());
    setToken(newToken);
    setUser(newUser);
  };

  // Auth API wrappers
  const register = async (phone, name, password) => {
    const res = await api.post('/auth/register', { phone, name, password });
    return res.data;
  };

  const verifySignupOTP = async (phone, otp) => {
    const res = await api.post('/auth/verify-otp', { phone, otp, purpose: 'signup' });
    const { token: newToken, user: newUser } = res.data.data;
    persistSession(newToken, newUser);
    return res.data;
  };

  const login = async (phone, password, rememberMe = false) => {
    const res = await api.post('/auth/login', { phone, password, remember_me: rememberMe });
    const { token: newToken, user: newUser } = res.data.data;
    persistSession(newToken, newUser);
    return res.data;
  };

  const forgotPassword = async (phone) => {
    const res = await api.post('/auth/forgot-password', { phone });
    return res.data;
  };

  const verifyResetOTP = async (phone, otp) => {
    const res = await api.post('/auth/verify-otp', { phone, otp, purpose: 'reset_password' });
    return res.data; // { data: { reset_token } }
  };

  const resetPassword = async (resetToken, newPassword) => {
    const res = await api.post('/auth/reset-password', { reset_token: resetToken, new_password: newPassword });
    return res.data;
  };

  const resendOTP = async (phone, purpose) => {
    const res = await api.post('/auth/resend-otp', { phone, purpose });
    return res.data;
  };

  const forceResetPassword = async (oldPassword, newPassword) => {
    const res = await api.post('/auth/force-reset-password', { old_password: oldPassword, new_password: newPassword });
    return res.data;
  };

  const logout = useCallback(() => {
    localStorage.removeItem('besq_token');
    localStorage.removeItem('besq_user');
    localStorage.removeItem('besq_last_activity');
    setToken(null);
    setUser(null);
  }, []);

  // Idle-timeout for customer sessions
  useEffect(() => {
    if (!user || user.role !== 'CUSTOMER') return undefined;
    const updateActivity = () => localStorage.setItem('besq_last_activity', Date.now().toString());
    const checkIdle = () => {
      const last = parseInt(localStorage.getItem('besq_last_activity') || '0', 10);
      if (last && Date.now() - last > CUSTOMER_IDLE_MS) {
        logout();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    };
    const events = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, updateActivity));
    const interval = setInterval(checkIdle, 30 * 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
      clearInterval(interval);
    };
  }, [user, logout]);

  const isAuthenticated = !!token;
  const ADMIN_ROLES = ['SUPER_ADMIN', 'BRANCH_ADMIN'];
  const STAFF_ROLES = ['SUPER_ADMIN', 'BRANCH_ADMIN', 'TAILOR', 'PICKUP_AGENT', 'SALES_AGENT'];
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isStaff = STAFF_ROLES.includes(user?.role);

  return (
    <AuthContext.Provider
      value={{
        user, token, loading, isAuthenticated, isAdmin, isStaff,
        register, verifySignupOTP, login, forgotPassword, verifyResetOTP, resetPassword, resendOTP, forceResetPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
